import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ServiceCard } from "@/components/admin/services/ServiceCard";
import { ServiceFormDialog } from "@/components/admin/services/ServiceFormDialog";
import { ServiceDeleteDialog } from "@/components/admin/services/ServiceDeleteDialog";
import { ServiceSkeletonCard } from "@/components/admin/services/ServiceSkeletonCard";
import { throwIfNotOk } from "@/lib/api-error";
import { Panel } from "./detail/Panel";
import type { Service } from "@/types";

export interface TherapistServicesSectionProps {
  therapistId: string;
}

export function TherapistServicesSection({ therapistId }: TherapistServicesSectionProps) {
  const queryClient = useQueryClient();
  const [editingService, setEditingService] = useState<Service | null>(null);
  const editServiceId = editingService?.id ?? null;
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const { data: services = [], isLoading } = useQuery({
    queryKey: ["admin", "therapist-services", therapistId],
    queryFn: async () => {
      const params = new URLSearchParams({ all: "1", therapistId });
      const res = await fetch(`/api/services?${params}`);
      await throwIfNotOk(res, "Greška pri učitavanju usluga");
      const env = (await res.json()) as { services: Service[] };
      return env.services;
    },
  });

  const { data: editService } = useQuery({
    queryKey: ["services", "detail", editServiceId],
    queryFn: async () => {
      const res = await fetch(`/api/services/${editServiceId}`);
      if (!res.ok) throw new Error("Failed to load service");
      return res.json() as Promise<Service>;
    },
    enabled: !!editServiceId,
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, ...data }: Partial<Service> & { id: string }) => {
      const res = await fetch(`/api/services/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      await throwIfNotOk(res, "Greška pri ažuriranju usluge");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "therapist-services", therapistId] });
      setEditingService(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/services/${id}`, { method: "DELETE" });
      await throwIfNotOk(res, "Greška pri brisanju usluge");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "therapist-services", therapistId] });
      setDeletingId(null);
    },
  });

  return (
    <Panel title="Usluge" count={isLoading ? undefined : services.length}>
      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2">
          {[...Array(2)].map((_, i) => (
            <ServiceSkeletonCard key={i} />
          ))}
        </div>
      ) : services.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Ovaj terapeut još nema usluga, pa ga klijenti ne mogu rezervisati.
        </p>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {services.map((service) => (
            <ServiceCard
              key={service.id}
              service={service}
              onEdit={() => setEditingService(service)}
              onDelete={() => setDeletingId(service.id)}
            />
          ))}
        </div>
      )}

      {editingService && (
        <ServiceFormDialog
          service={editService ?? editingService}
          isPending={updateMutation.isPending}
          onSubmit={(data) => updateMutation.mutate({ id: editingService.id, ...data })}
          onClose={() => setEditingService(null)}
        />
      )}

      {deletingId && (
        <ServiceDeleteDialog
          isPending={deleteMutation.isPending}
          onConfirm={() => deleteMutation.mutate(deletingId)}
          onClose={() => setDeletingId(null)}
        />
      )}
    </Panel>
  );
}
